import { useCallback } from 'react';
import { settingsService } from '../../../services/settingsService';
import { useSnackbar } from '../../../components/common/Snackbar';
import { validatePhoneNumber, extractErrorMessage } from '../../../utils/settingsUtils';

/**
 * Hook to manage profile save logic for Settings page
 * @param {Object} params - Hook parameters
 * @param {Object} params.profileData - Profile data object
 * @param {Object} params.user - Current user object
 * @param {Function} params.updateUser - Function to update user in AuthContext
 * @param {Function} params.setIsSavingProfile - Setter for saving state
 * @param {Function} params.setIsEditingProfile - Setter for editing state
 * @returns {Function} Save profile handler
 */
export const useSettingsProfile = ({
  profileData,
  user,
  updateUser,
  setIsSavingProfile,
  setIsEditingProfile,
}) => {
  const { showSnackbar } = useSnackbar();

  const handleSaveProfile = useCallback(async () => {
    // Validate phone number if provided
    if (profileData.phone && profileData.phone.trim() !== '' && !validatePhoneNumber(profileData.phone)) {
      showSnackbar('Please enter a valid phone number (10-15 digits, optional + prefix)', 'error');
      return;
    }

    setIsSavingProfile(true);
    try {
      // Get user ID from user object (from auth context) or localStorage
      const userId = user?._id || user?.id || null;
      // Create payload with whatsappNumber set to phone number, and include designation/department from user (disabled fields)
      const payloadData = {
        name: profileData.name,
        full_name: profileData.name,
        email: profileData.email,
        phone: profileData.phone || '', // Send empty string if not provided
        whatsappNumber: profileData.phone || '', // Set whatsappNumber to phone number (or empty if not provided)
        designation: user?.designation || '', // Include designation from user (disabled field)
        department: user?.department || '', // Include department from user (disabled field)
      };
      const response = await settingsService.updatePersonalDetails(payloadData, userId);

      // Update localStorage and AuthContext with the updated user data
      if (response) {
        const updatedUser = { ...user, ...response };
        // Update AuthContext state (which also updates localStorage)
        updateUser(updatedUser);

        // Show success message - use backend message if available, otherwise custom
        const successMessage = response?.message || 'Personal details updated successfully';
        showSnackbar(successMessage, 'success');
      }

      setIsEditingProfile(false);
    } catch (error) {
      const errorMessage = extractErrorMessage(error, 'Failed to update personal details');
      showSnackbar(errorMessage, 'error');
      // console.error('Error updating personal details:', error);
    } finally {
      setIsSavingProfile(false);
    }
  }, [profileData, user, updateUser, setIsSavingProfile, setIsEditingProfile, showSnackbar]);

  return {
    handleSaveProfile,
  };
};
